import { useState, useMemo, useRef } from "react"
import {
  Typography,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popper,
  Paper,
  ClickAwayListener,
  TextField,
  InputAdornment,
} from "@mui/material"
import { ExpandMore, ExpandLess, Search, AccountBalance as AccountBalanceIcon } from "@mui/icons-material"

function ClientBalanceCard({ stats }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const anchorRef = useRef(null)

  const clients = stats?.clients || []
  const totalClients = stats?.count || clients.length

  // Filter clients by name
  const filteredClients = useMemo(() => {
    return clients.filter((client) =>
      client.name?.toLowerCase().includes(search.toLowerCase())
    )
  }, [clients, search])

  return (
    <>
      <Box
        sx={{
          backgroundColor: "#ffffff",
          border: "1px solid #f1f5f9",
          borderRadius: "12px",
          p: 2.5,
          height: "100%",
          boxShadow: "0 1px 3px 0 rgba(15,23,42,0.05)",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 10px 15px -3px rgba(15,23,42,0.08)",
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography
              variant="overline"
              sx={{ fontWeight: 600, letterSpacing: "0.06em", color: "#64748b", display: "block", mb: 0.5 }}
            >
              Current Client Balance
            </Typography>

            <Typography variant="h3" component="p" sx={{ fontWeight: 700, color: "#0f172a", lineHeight: 1.1, mb: 0.5 }}>
              ₹{(stats?.totalBalanceCarried || 0).toLocaleString()}
            </Typography>

            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
              {totalClients} client{totalClients !== 1 ? "s" : ""} with balance
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "row", marginY: "auto", alignItems: "center" }}>
            <IconButton
              ref={anchorRef}
              onClick={() => setOpen((prev) => !prev)}
              size="small"
              sx={{ color: "#64748b" }}
            >
              {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
            <Box
              sx={{
                backgroundColor: "#fef3c7",
                color: "#d97706",
                width: 52,
                height: 52,
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 0.5,
              }}
            >
              <AccountBalanceIcon sx={{ fontSize: 26 }} />
            </Box>

          </Box>
        </Box>
      </Box>

      {/* Floating dropdown */}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper
            sx={{
              mt: 1,
              width: 300,
              maxHeight: 350,
              display: "flex",
              flexDirection: "column",
              borderRadius: 2,
              boxShadow: 3,
            }}
          >
            {/* Search Bar */}
            <Box sx={{ p: 1.5 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search client..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* List */}
            <Box
              sx={{
                overflowY: "auto",
                maxHeight: 280,

                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#ccc",
                  borderRadius: 3,
                },
              }}
            >
              <List dense>
                {filteredClients.length > 0 ? (
                  filteredClients.map((client) => (
                    <ListItem key={client._id} divider>
                      <ListItemText
                        primary={client.name}
                        secondary={`₹${client.balance.toLocaleString()}`}
                      />
                    </ListItem>
                  ))
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ p: 2, textAlign: "center", color: "text.secondary" }}
                  >
                    No clients found
                  </Typography>
                )}
              </List>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  )
}

export default ClientBalanceCard